import React, { useEffect } from "react";
import PropTypes from "prop-types";
import "./customModal.scss";

// --- Main Modal Wrapper ---
export const Modal = ({ isOpen, backdrop = true, className = "", children }) => {
    // When the modal opens, lock body scroll. Use a counter on the body to support multiple modals.
    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (typeof window === 'undefined') return;
        const body = document.body;
        const attr = 'data-mmh-modal-open-count';
        const scrollAttr = 'data-mmh-modal-scroll-y';
        const getCount = () => parseInt(body.getAttribute(attr) || '0', 10);

        if (!isOpen) return; // only act when opening

        const prev = getCount();
        body.setAttribute(attr, String(prev + 1));

        if (prev === 0) {
            // first modal opened -> lock scroll
            try {
                const scrollY = window.scrollY || window.pageYOffset || 0;
                // store value so we can restore later
                body.setAttribute(scrollAttr, String(scrollY));
                // fix body to prevent scroll and layout shift
                body.style.position = 'fixed';
                body.style.top = `-${scrollY}px`;
                body.style.left = '0';
                body.style.right = '0';
                body.style.width = '100%';
                // also hide overflow as a fallback
                body.style.overflow = 'hidden';
            } catch (e) {
                // fallback: just hide overflow
                body.style.overflow = 'hidden';
            }
        }

        // cleanup: decrement counter when this modal closes/unmounts
        return () => {
            const cur = getCount();
            const next = Math.max(0, cur - 1);
            if (next === 0) {
                // last modal closed -> restore body styles and scroll
                try {
                    const saved = parseInt(body.getAttribute(scrollAttr) || '0', 10) || 0;
                    body.style.position = '';
                    body.style.top = '';
                    body.style.left = '';
                    body.style.right = '';
                    body.style.width = '';
                    body.style.overflow = '';
                    body.removeAttribute(attr);
                    body.removeAttribute(scrollAttr);
                    // restore scroll position
                    window.scrollTo(0, saved);
                } catch (e) {
                    body.style.overflow = '';
                    body.removeAttribute(attr);
                    body.removeAttribute(scrollAttr);
                }
            } else {
                body.setAttribute(attr, String(next));
            }
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className={`custom-modal-backdrop ${backdrop ? "show" : ""}`}>
            <div className={`custom-modal ${className}`}>
                {children}
            </div>
        </div>
    );
};

// --- Header ---
export const ModalHeader = ({ children, toggle }) => {
    return (
        <div className="custom-modal-header">
            {children}
            {toggle && (
                <button className="custom-close-btn" onClick={toggle}>
                    &times;
                </button>
            )}
        </div>
    );
};

// --- Body ---
export const ModalBody = ({ children, className }) => {
    return <div className={`custom-modal-body ${className}`}>{children}</div>;
};

// --- Footer ---
export const ModalFooter = ({ children }) => {
    return <div className="custom-modal-footer">{children}</div>;
};

// --- PropTypes (optional) ---
Modal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    backdrop: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    className: PropTypes.string,
    children: PropTypes.node,
};

 ModalHeader.propTypes = {
    children: PropTypes.node,
    toggle: PropTypes.func,
};

 ModalBody.propTypes = {
    children: PropTypes.node,
};

ModalFooter.propTypes = {
    children: PropTypes.node,
};
